import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthSessionService } from './auth-session.service';
import { isF1Tier, isF2Tier, isOccupantTier, isVendorFieldTier } from './auth.models';

export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  if (auth.isAuthenticated()) {
    return true;
  }
  return router.createUrlTree(['/login']);
};

export const occupantPersonaGuard: CanActivateFn = () => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  const session = auth.snapshot;
  if (!session) {
    return router.createUrlTree(['/login']);
  }
  if (!isOccupantTier(session.authorityTier) || !session.personas.includes('occupant')) {
    return router.createUrlTree(['/login']);
  }
  return true;
};

/** Services shell: signed-in vendor field tier (F1/F2) with the services persona. */
export const servicesPersonaGuard: CanActivateFn = () => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  const session = auth.snapshot;
  if (!session) {
    return router.createUrlTree(['/login']);
  }
  if (!isVendorFieldTier(session.authorityTier)) {
    return servicesAccessRefusedTree(router, 'tier');
  }
  if (!session.personas.includes('services')) {
    return router.createUrlTree(['/shell/persona']);
  }
  return true;
};

export const vendorFieldTierGuard: CanActivateFn = () => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  const session = auth.snapshot;
  if (!session) {
    return router.createUrlTree(['/login']);
  }
  if (isVendorFieldTier(session.authorityTier)) {
    return true;
  }
  return servicesAccessRefusedTree(router, 'tier');
};

/** F1 service manager routes (staff, tickets, assign, create, stores, …). */
export const servicesF1TierGuard: CanActivateFn = () => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  const session = auth.snapshot;
  if (!session) {
    return router.createUrlTree(['/login']);
  }
  if (isF1Tier(session.authorityTier)) {
    return true;
  }
  return servicesAccessRefusedTree(router, 'f1-required');
};

/** F2 field executive routes (execute work orders, zone scan, …). */
export const servicesF2TierGuard: CanActivateFn = () => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  const session = auth.snapshot;
  if (!session) {
    return router.createUrlTree(['/login']);
  }
  if (isF2Tier(session.authorityTier)) {
    return true;
  }
  return servicesAccessRefusedTree(router, 'f2-required');
};

function servicesAccessRefusedTree(router: Router, reason: string) {
  return router.createUrlTree(['/services/access-refused'], { queryParams: { reason } });
}
